import React, { useState } from "react";
import axios from "axios";
import "./css/ZomatoRagChatbot.css";

const DEFAULT_CHAT_MODEL = "claude-3-haiku-20240307";
const DEFAULT_EMBEDDING_MODEL = "sentence-transformers/all-mpnet-base-v2";

export default function ZomatoRagChatbot() {
  const [anthropicKey, setAnthropicKey] = useState("");
  const [hfKey, setHfKey] = useState("");
  const [chatModel, setChatModel] = useState(DEFAULT_CHAT_MODEL);
  const [embeddingModel, setEmbeddingModel] = useState(DEFAULT_EMBEDDING_MODEL);

  const [messages, setMessages] = useState([
    { role: "system", content: "Welcome! Please enter config and chat." }
  ]);
  const [userInput, setUserInput] = useState("");
  const [loadingChat, setLoadingChat] = useState(false);
  const [error, setError] = useState("");

  // Validate config fields are filled
  const chatEnabled =
    anthropicKey.trim() !== "" &&
    hfKey.trim() !== "" &&
    chatModel.trim() !== "" &&
    embeddingModel.trim() !== "";

  const handleSend = async (e) => {
    e.preventDefault();
    if (!userInput.trim()) return;
    if (!chatEnabled) {
      setError("Please fill all configuration fields before chatting.");
      return;
    }

    const userMsg = { role: "user", content: userInput };
    setMessages((prev) => [...prev, userMsg]);
    setUserInput("");
    setLoadingChat(true);
    setError("");

    try {
      const payload = {
        anthropicKey,
        hfKey,
        chatModel,
        embeddingModel,
        message: userMsg.content,
      };

      const apiChatUrl = process.env.REACT_APP_API_CHAT

      const response = await axios.post(`${apiChatUrl}`, payload);

      setMessages((prev) => [
        ...prev,
        { role: "ai", content: response.data.reply || "No answer from AI." },
      ]);
    } catch (err) {
      setMessages((prev) => [
        ...prev,
        {
          role: "ai",
          content: "Error occurred while fetching response.",
        },
      ]);
      setError(err.response?.data?.error || err.message || "Server error during chat.");
    } finally {
      setLoadingChat(false);
    }
  };

  return (
    <div className="zomato-rag-bg">
      <div className="zomato-rag-card">
        <h2 className="zomato-title">Zomato Bangalore — RAG Restaurant Chatbot</h2>
        <p className="zomato-desc">
          Retrieval-Augmented Generation chatbot on Bangalore restaurants dataset.
        </p>

        <form className="zomato-config-form">
          <div className="config-group">
            <label>
              Anthropic API Key
              <input
                type="password"
                value={anthropicKey}
                onChange={(e) => setAnthropicKey(e.target.value)}
                placeholder="Enter your Anthropic API key"
                autoComplete="off"
                required
                disabled={loadingChat}
              />
            </label>
            <label>
              HuggingFace API Token
              <input
                type="password"
                value={hfKey}
                onChange={(e) => setHfKey(e.target.value)}
                placeholder="Enter your HuggingFace Hub token"
                autoComplete="off"
                required
                disabled={loadingChat}
              />
            </label>
          </div>
          <div className="config-group">
            <label>
              Chat model
              <select
                value={chatModel}
                onChange={(e) => setChatModel(e.target.value)}
                disabled={loadingChat}
                className="options"
              >
                <option value="claude-3-haiku-20240307">Claude 3 Haiku (recommended)</option>
                <option value="claude-3-sonnet-20240229">Claude 3 Sonnet</option>
                <option value="gpt-3.5-turbo">OpenAI GPT-3.5 Turbo</option>
              </select>
            </label>
            <label>
              Embedding model
              <select
                value={embeddingModel}
                onChange={(e) => setEmbeddingModel(e.target.value)}
                disabled={loadingChat}
                className="options"
              >
                <option value="sentence-transformers/all-mpnet-base-v2">
                  all-mpnet-base-v2 (recommended)
                </option>
                <option value="sentence-transformers/paraphrase-multilingual-MiniLM-L12-v2">
                  MiniLM-L12-v2
                </option>
              </select>
            </label>
          </div>
        </form>

        {error && <div className="error-box">{error}</div>}

        <div className="chat-box">
          <div className="chat-messages">
            {messages.map((msg, i) => (
              <div key={i} className={`chat-msg ${msg.role}`}>
                <span>
                  {msg.role === "user"
                    ? "You"
                    : msg.role === "system"
                    ? "System"
                    : "AI"}
                  :
                </span>
                <span> {msg.content}</span>
              </div>
            ))}
            {loadingChat && (
              <div className="chat-msg ai">
                <span>AI:</span> <span>Typing...</span>
              </div>
            )}
          </div>
          <form className="chat-input-form" onSubmit={handleSend} autoComplete="off">
            <input
              value={userInput}
              onChange={(e) => setUserInput(e.target.value)}
              placeholder="Ask about a restaurant, cuisine, location..."
              disabled={!chatEnabled || loadingChat}
              autoFocus
            />
            <button
              type="submit"
              disabled={!chatEnabled || loadingChat || !userInput.trim()}
            >
              Send
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
